import React, { useEffect, useRef, useState } from "react";
import DOMPurify from "isomorphic-dompurify";
import { motion } from "framer-motion";
import { Headphones, Pause, Square, AudioLines, Loader2 } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { trackTtsPlay } from "../lib/analytics";

import { API } from "../lib/api";
// API now resolved at runtime via /lib/api.js
// Strip HTML tags & decode entities to clean text for TTS.
// Sanitized via DOMPurify before assignment for defense-in-depth, even though
// the element is never inserted into the DOM (so scripts cannot execute).
const htmlToPlainText = (html) => {
  if (typeof window === "undefined") return "";
  const tmp = document.createElement("div");
  tmp.innerHTML = DOMPurify.sanitize(html);
  return (tmp.textContent || tmp.innerText || "").replace(/\s+/g, " ").trim();
};

/**
 * AudioReader — OpenAI TTS (onyx voice via backend /api/tts)
 * Cinematic player with play / pause / stop and shimmer states.
 * Props: text, lang, htmlText, labels { listen, pause, stop, playing }, voice
 */
export const AudioReader = ({ text, lang = "en", htmlText, labels = {}, voice = "onyx", onAfterPlay, trialRemaining = null }) => {
  const { token, isPro } = useAuth();
  const [state, setState] = useState("idle"); // idle | loading | playing | paused | error
  const audioRef = useRef(null);
  const blobUrlRef = useRef(null);
  const abortRef = useRef(null);

  // Cleanup on unmount / when text changes
  const cleanup = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = "";
      audioRef.current = null;
    }
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
    }
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
  };

  useEffect(() => {
    return cleanup;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // When text/lang changes mid-play, reset
    cleanup();
    setState("idle");
  }, [text, htmlText, lang, voice]);

  const play = async () => {
    const plain = htmlText ? htmlToPlainText(htmlText) : (text || "").trim();
    if (!plain) return;

    // If we already have audio loaded and are paused, resume
    if (audioRef.current && state === "paused") {
      try {
        await audioRef.current.play();
        setState("playing");
      } catch {
        setState("error");
      }
      return;
    }

    cleanup();
    setState("loading");
    const ctrl = new AbortController();
    abortRef.current = ctrl;

    try {
      const res = await fetch(`${API}/tts`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ text: plain.slice(0, 4096), voice, model: "tts-1", speed: 1.0 }),
        signal: ctrl.signal,
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      blobUrlRef.current = url;

      // GA4 — track TTS playback
      try { trackTtsPlay({ lessonKey: null, isPro }); } catch {}

      // Notify parent that the trial may have been consumed
      if (onAfterPlay) {
        try { onAfterPlay(); } catch {}
      }

      const audio = new Audio(url);
      audio.preload = "auto";
      audio.onended = () => setState("idle");
      audio.onerror = () => setState("error");
      audio.onpause = () => {
        // Only mark paused if not ended
        if (!audio.ended) setState((s) => (s === "playing" ? "paused" : s));
      };
      audio.onplaying = () => setState("playing");
      audioRef.current = audio;

      await audio.play();
      setState("playing");
    } catch (e) {
      if (e.name === "AbortError") return;
      // If trial exhausted, refresh user so parent swaps to PremiumGate
      if (e.message?.includes("402") && onAfterPlay) {
        try { onAfterPlay(); } catch {}
      }
      console.warn("TTS error", e);
      setState("error");
    }
  };

  const pause = () => {
    if (!audioRef.current) return;
    if (state === "playing") {
      audioRef.current.pause();
      setState("paused");
    } else if (state === "paused") {
      audioRef.current.play().then(() => setState("playing")).catch(() => setState("error"));
    }
  };

  const stop = () => {
    cleanup();
    setState("idle");
  };

  const isActive = state === "playing" || state === "paused";
  const isLoading = state === "loading";

  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="audio-reader">
      {!isActive && !isLoading && (
        <>
          <motion.button
            onClick={play}
            whileTap={{ scale: 0.97 }}
            className="group relative inline-flex items-center gap-2.5 overflow-hidden rounded-full border border-cyan-400/40 bg-gradient-to-r from-cyan-500/15 via-cyan-400/10 to-purple-500/15 px-5 py-2.5 text-sm font-bold text-white shadow-[0_0_24px_rgba(0,229,255,0.25)] transition-all hover:-translate-y-0.5 hover:border-cyan-300 hover:shadow-[0_0_36px_rgba(0,229,255,0.55)]"
            data-testid="audio-reader-play-btn"
            title={labels.listen || "Listen"}
          >
            <span className="relative flex h-7 w-7 items-center justify-center rounded-full bg-cyan-500/20 ring-1 ring-cyan-300/40">
              <Headphones className="h-3.5 w-3.5 text-cyan-200" />
              <span className="absolute inset-0 animate-ping rounded-full bg-cyan-400/30" />
            </span>
            <span className="text-xs uppercase tracking-[0.2em]">
              {labels.listen || "Listen to lesson"}
            </span>
            <AudioLines className="h-4 w-4 text-cyan-300 opacity-70 transition group-hover:opacity-100" />
          </motion.button>
          {trialRemaining !== null && trialRemaining > 0 && (
            <span
              className="inline-flex items-center gap-1 rounded-full border border-amber-400/40 bg-amber-400/10 px-2.5 py-1.5 text-[10px] font-bold uppercase tracking-[0.25em] text-amber-200"
              data-testid="audio-reader-trial-badge"
            >
              🎁 {labels.trial_one || "1 free try"}
            </span>
          )}
        </>
      )}

      {isLoading && (
        <div
          className="inline-flex items-center gap-2.5 rounded-full border border-cyan-400/40 bg-cyan-400/10 px-5 py-2.5 text-xs font-bold uppercase tracking-[0.2em] text-cyan-200 backdrop-blur"
          data-testid="audio-reader-loading"
        >
          <Loader2 className="h-4 w-4 animate-spin" />
          {labels.loading || labels.listen || "Generating audio…"}
        </div>
      )}

      {isActive && (
        <>
          <div className="inline-flex items-center gap-2 rounded-full border border-emerald-400/40 bg-emerald-400/10 px-3 py-1.5 text-xs font-bold uppercase tracking-[0.2em] text-emerald-300 backdrop-blur">
            <span className="relative flex h-1.5 w-1.5">
              {state === "playing" && (
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
              )}
              <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500" />
            </span>
            <AudioLines className="h-3.5 w-3.5" />
            {state === "paused" ? (labels.paused || labels.pause || "Paused") : (labels.playing || "Playing…")}
          </div>
          <button
            onClick={pause}
            className="inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-white/5 px-3 py-1.5 text-xs font-bold uppercase tracking-[0.2em] text-white/80 transition-all hover:border-white/40 hover:bg-white/10"
            data-testid="audio-reader-pause-btn"
          >
            <Pause className="h-3 w-3" />
            {labels.pause || "Pause"}
          </button>
          <button
            onClick={stop}
            className="inline-flex items-center gap-1.5 rounded-full border border-red-400/30 bg-red-400/5 px-3 py-1.5 text-xs font-bold uppercase tracking-[0.2em] text-red-300 transition-all hover:border-red-400/60 hover:bg-red-400/10"
            data-testid="audio-reader-stop-btn"
          >
            <Square className="h-3 w-3" />
            {labels.stop || "Stop"}
          </button>
        </>
      )}

      {state === "error" && (
        <button
          onClick={play}
          className="inline-flex items-center gap-1.5 rounded-full border border-red-400/30 bg-red-400/5 px-3 py-1.5 text-xs font-bold uppercase tracking-[0.2em] text-red-300 transition-all hover:border-red-400/60 hover:bg-red-400/10"
          data-testid="audio-reader-retry-btn"
        >
          {labels.retry || "Retry"}
        </button>
      )}
    </div>
  );
};

export default AudioReader;
